import ClickAwayListener from '@mui/material/ClickAwayListener'
import type { PopperPlacementType } from '@mui/material/Popper'
import Popper from '@mui/material/Popper'
import { forwardRef, KeyboardEvent, ReactNode, RefObject, useCallback, useEffect, useId, useRef } from 'react'
import styles from '../IntradayTradingView.module.less'

type IntradayHeaderPopoverProps = {
  anchorEl: HTMLElement | null
  onClose: () => void
  children: ReactNode
  title?: string
  placement?: PopperPlacementType
  horizontalOffset?: number
  /**
   * Refs to elements that should NOT trigger close when clicked (e.g. the toggle buttons,
   * or the sibling popover paper). Clicks inside any of these elements are excluded from
   * click-away handling.
   */
  clickAwayExcludeRefs?: RefObject<HTMLElement | null>[]
}

export const IntradayHeaderPopover = forwardRef<HTMLDivElement, IntradayHeaderPopoverProps>(
  function IntradayHeaderPopover(
    { anchorEl, onClose, children, title, placement = 'bottom-start', horizontalOffset = 0, clickAwayExcludeRefs },
    ref
  ) {
    const paperRef = useRef<HTMLDivElement>(null)
    const titleId = useId()

    useEffect(() => {
      if (!anchorEl) {
        return
      }
      paperRef.current?.focus()
    }, [anchorEl])

    useEffect(() => {
      if (!anchorEl) {
        return
      }

      const handleDocumentKeyDown = (event: globalThis.KeyboardEvent): void => {
        if (event.key === 'Escape') {
          onClose()
        }
      }

      document.addEventListener('keydown', handleDocumentKeyDown)
      return () => {
        document.removeEventListener('keydown', handleDocumentKeyDown)
      }
    }, [anchorEl, onClose])

    const handleClickAway = useCallback(
      (event: MouseEvent | TouchEvent): void => {
        if (clickAwayExcludeRefs?.some((ref) => ref.current?.contains(event.target as Node))) {
          return
        }
        onClose()
      },
      [clickAwayExcludeRefs, onClose]
    )

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Escape') {
          onClose()
        }
      },
      [onClose]
    )

    if (!anchorEl) {
      return null
    }

    return (
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement={placement}
        style={{ zIndex: 4 }}
        modifiers={[{ name: 'offset', options: { offset: [horizontalOffset, 0] } }]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <div
            ref={(node) => {
              paperRef.current = node
              if (typeof ref === 'function') {
                ref(node)
              } else if (ref) {
                ref.current = node
              }
            }}
            className={styles.IntradayTradingView_HeaderPopoverPaper}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            role="dialog"
            aria-labelledby={title ? titleId : undefined}
          >
            {title ? (
              <div className={styles.IntradayTradingView_HeaderPopoverHeader}>
                <h3 id={titleId} className={styles.IntradayTradingView_HeaderPopoverTitle}>
                  {title}
                </h3>
              </div>
            ) : null}
            <div className={styles.IntradayTradingView_HeaderPopoverContent}>{children}</div>
          </div>
        </ClickAwayListener>
      </Popper>
    )
  }
)
